/**
 * Per-mode and per-tier photo caps for a single analysis request.
 *
 * Two independent axes:
 *  - MODE_PHOTO_CEILING is the absolute prompt/cost ceiling. Weed's 3 predates
 *    the tier ladder (one overview + two supporting views, see
 *    lib/analysis/weed-photo-accumulation.ts); identify/diagnose use
 *    MAX_ANALYSIS_IMAGES, which used to live inline in the analyze route.
 *  - TIER_PHOTO_CAPS is the monetized ladder: anonymous 1, registered free 3,
 *    premium 5.
 *
 * Weed is deliberately exempt from the tier ladder. Its accumulation loop ships
 * free to anonymous users (mobile/app/scan/weed.tsx addNextPhoto) with copy
 * promising exactly that, so applying the anonymous cap of 1 there would brick
 * a shipped flow. The tier ladder therefore governs identify and diagnose only.
 *
 * This is a different axis from the monthly analysis-count ladder in
 * lib/auth/feature-flags.ts. Do not conflate the two.
 */

import type { AnalysisMode } from '@/lib/types/diagnosis'

export type PhotoTier = 'anonymous' | 'registered_free' | 'premium'

export const MAX_ANALYSIS_IMAGES = 5

/** Absolute per-mode ceiling, independent of who is asking. */
export const MODE_PHOTO_CEILING: Record<AnalysisMode, number> = {
  identify: MAX_ANALYSIS_IMAGES,
  diagnose: MAX_ANALYSIS_IMAGES,
  weed: 3,
}

export const TIER_PHOTO_CAPS: Record<PhotoTier, number> = {
  anonymous: 1,
  registered_free: 3,
  premium: 5,
}

export function resolvePhotoCap(input: {
  mode: AnalysisMode
  tier: PhotoTier
  /** RATE_LIMIT_ALLOWLIST IPs test on prod against the raw ceiling. */
  allowlisted?: boolean
}): number {
  const ceiling = MODE_PHOTO_CEILING[input.mode] ?? MODE_PHOTO_CEILING.identify
  if (input.mode === 'weed') return ceiling
  if (input.allowlisted) return ceiling
  return Math.min(ceiling, TIER_PHOTO_CAPS[input.tier])
}

/**
 * The lowest tier that could send `requested` photos for this mode, or null
 * when even premium cannot. Drives the routing fields on the 403 body so the
 * clients know whether to open sign-in or the paywall.
 */
export function tierRequiredForPhotoCount(
  mode: AnalysisMode,
  requested: number,
): PhotoTier | null {
  const ceiling = MODE_PHOTO_CEILING[mode] ?? MODE_PHOTO_CEILING.identify
  if (requested <= TIER_PHOTO_CAPS.anonymous) return 'anonymous'
  if (requested <= Math.min(ceiling, TIER_PHOTO_CAPS.registered_free)) return 'registered_free'
  if (requested <= ceiling) return 'premium'
  return null
}

/**
 * Why the 403 fired. Both denials share `code: 'photo_limit_reached'`, but they
 * are different stories: 'photo_count' means this request carried more photos
 * than the tier reads at once, while 'additive_cap' means the per-root free
 * refinement allowance is spent. Clients pick their copy from this, so a spent
 * refinement never renders as "you sent 3 views and your plan reads 3".
 * Optional on the type because older payloads carry no reason.
 */
export type PhotoLimitReason = 'photo_count' | 'additive_cap'

/** Wire shape of the 403 `photoLimit` object. Shared by both clients. */
export interface PhotoLimitDetail {
  cap: number
  requested: number
  tier: PhotoTier
  mode: AnalysisMode
  requiredTier: PhotoTier | null
  reason?: PhotoLimitReason
}
